import { useEffect, useRef, useSyncExternalStore } from 'react';
import { LogIn, LogOut, Users, Coffee, Briefcase, Plane, type LucideIcon } from '../../icons';
import { Avatar } from '../shared';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useAuthStore } from '../../store/authStore';
import { useWorkStatusStore } from '../../store/workStatusStore';
import { STATUS_BAR_KEYS, WORK_STATUS_TONE, isCheckOutLocked, type WorkStatusKey } from '../../lib/work-status';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../lib/layout-i18n';
import './StatusDropdown.css';

/**
 * ⑥ 상태바 근무 상태 드롭다운.
 *
 * 2026-08-12 최초 — "상태바에서 상태를 클릭하면 출근, 퇴근, 회의 등의 상태가 표시되도록".
 * **2026-08-20 개편(사용자 요구)** — "내 정보 다른 디자인으로 / 근무상태도 출근, 퇴근 이렇게
 * 보이면 되는데 근무상태 아이콘 필요없음".
 *
 * 그래서 세 가지가 바뀌었다:
 *   1) 머리에 **내 정보 카드**(아바타·이름·이메일·현재 상태)를 둔다. 종전 "근무 상태" 제목 한 줄은
 *      누구의 상태인지 말해 주지 않았다.
 *   2) 그 아래 **오늘 기록**(출근/퇴근 시각)을 텍스트로 보여 준다 — 상태만으로는 몇 시에 찍었는지
 *      알 수 없어 매번 근태 화면으로 가야 했다.
 *   3) 고르는 항목은 **출근·퇴근 둘뿐**이고 **아이콘을 쓰지 않는다**(`STATUS_BAR_KEYS`).
 *
 * ⚠ 회의중·자리비움·외근·출장은 **없어지지 않았다.** 프로필 팝오버가 `WORK_STATUS_KEYS` 로 그대로
 *   제공하고, 거기서 고른 값은 이 드롭다운 머리의 현재 상태와 상태바에 그대로 표시된다.
 *   `ICON_BY_STATUS` 를 여기서 계속 export 하는 이유도 그 팝오버가 이 표를 쓰기 때문이다.
 *
 * **2026-08-23 — 퇴근 잠금.** 오늘 출근 기록이 없으면 퇴근을 고를 수 없다(근태 2차 설계 요구 3 ·
 * 화면 E). 서버는 원래 `NOT_CHECKED_IN` 400 으로 거절했는데 화면이 말해 주지 않아 눌러 봐야
 * 알았다. 판정은 `lib/work-status.isCheckOutLocked` — 오늘 기록을 읽어 낸 뒤에만 잠근다.
 *
 * 상호작용 계약(위로 열림 · 바깥 클릭 닫기 · `useModalLeave(160)`)은 알림·채팅 드롭다운과 같게
 * 유지한다 — 같은 바에 나란히 선 버튼이 저마다 다르게 움직이면 학습한 규칙이 깨진다.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** 여는 방향 — 'up' 은 하단 상태바 위로(기본은 'down'). */
  placement?: 'down' | 'up';
}

/**
 * 상태 → 아이콘. **이 드롭다운은 더 이상 쓰지 않는다**(2026-08-20) — 프로필 팝오버가 쓴다.
 * 지우면 팝오버가 깨지므로 export 를 유지한다.
 */
export const ICON_BY_STATUS: Record<WorkStatusKey, LucideIcon> = {
  work: LogIn,
  meeting: Users,
  away: Coffee,
  out: Briefcase,
  // 출장(2026-08-12) — 외근(Briefcase)과 한눈에 갈리는 은유. 프로필 팝오버도 이 표를 함께 쓴다.
  trip: Plane,
  off: LogOut,
};

export function statusLabel(key: WorkStatusKey): string {
  return tLayout(`layout.workstatus.${key}`);
}

const NO_TIME = '--:--';

export function StatusDropdown({ open, onClose, placement = 'down' }: Props): JSX.Element | null {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const user = useAuthStore((s) => s.user);
  const status = useWorkStatusStore((s) => s.status);
  const pending = useWorkStatusStore((s) => s.pending);
  const error = useWorkStatusStore((s) => s.error);
  const today = useWorkStatusStore((s) => s.today);
  const hydrated = useWorkStatusStore((s) => s.hydrated);
  // 오늘이 간주 출근일(승인된 출장·외근)이면 퇴근이 열린다 — 근태 D2, 2026-08-24.
  const deemed = useWorkStatusStore((s) => s.deemed);
  const select = useWorkStatusStore((s) => s.select);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { shouldRender, isLeaving } = useModalLeave(open, 160);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onPointer); };
  }, [open, onClose]);

  if (!shouldRender) return null;

  // 서버 왕복이 필요한 항목(출근/퇴근)만 실패할 수 있다 → 성공 시에만 닫는다.
  const pick = (key: WorkStatusKey) => {
    void select(key).then(() => {
      if (!useWorkStatusStore.getState().error) onClose();
    });
  };

  // 퇴근은 출근한 사람만 — 오늘 기록을 읽어 낸 뒤에만 잠근다(근태 2차 설계 화면 E, 2026-08-23).
  const offLocked = isCheckOutLocked({ hydrated, checkIn: today.checkIn, deemed: Boolean(deemed) });

  const name = user?.name ?? '';
  const tone = status ? WORK_STATUS_TONE[status] : 'none';
  const currentLabel = status ? statusLabel(status) : tLayout('layout.workstatus.none');

  return (
    <div
      className={`status-dropdown status-dropdown--${placement}${isLeaving ? ' status-dropdown--leaving' : ''}`}
      ref={containerRef}
      role="dialog"
      aria-label={tLayout('layout.workstatus.aria')}
      aria-hidden={isLeaving || undefined}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── 내 정보 ── */}
      <div className="status-dropdown-me">
        <Avatar userId={user?.id ?? 0} name={name || '?'} src={user?.avatarUrl} size="md" />
        <span className="status-dropdown-me__body">
          <span className="status-dropdown-me__name">{name || tLayout('layout.workstatus.me')}</span>
          <span className="status-dropdown-me__sub" title={user?.email ?? ''}>{user?.email ?? ''}</span>
        </span>
        <span className={`status-dropdown-me__now is-${tone}`}>{currentLabel}</span>
      </div>

      {/* ── 오늘 기록 ── 상태만으로는 몇 시에 찍었는지 알 수 없다. */}
      <div className="status-dropdown-today">
        <span className="status-dropdown-today__k">{tLayout('layout.workstatus.today')}</span>
        <span className="status-dropdown-today__cell">
          <span className="status-dropdown-today__t">{statusLabel('work')}</span>
          <b className={`status-dropdown-today__v${today.checkIn ? '' : ' is-empty'}`}>{today.checkIn ?? NO_TIME}</b>
        </span>
        <span className="status-dropdown-today__cell">
          <span className="status-dropdown-today__t">{statusLabel('off')}</span>
          <b className={`status-dropdown-today__v${today.checkOut ? '' : ' is-empty'}`}>{today.checkOut ?? NO_TIME}</b>
        </span>
      </div>

      {/* ── 출근 / 퇴근 ── 아이콘 없음. 선택은 배경으로 표시한다. */}
      <div className="status-dropdown-pick" role="listbox" aria-label={tLayout('layout.workstatus.title')}>
        {STATUS_BAR_KEYS.map((key) => {
          const selected = status === key;
          const busy = pending === key;
          const locked = key === 'off' && offLocked;
          return (
            <button
              key={key}
              type="button"
              role="option"
              aria-selected={selected}
              className={`status-dropdown-pick__btn is-${WORK_STATUS_TONE[key]}${selected ? ' is-selected' : ''}`}
              disabled={pending !== null || locked}
              onClick={() => pick(key)}
            >
              <span className="status-dropdown-pick__label">{statusLabel(key)}</span>
              <span className="status-dropdown-pick__desc">
                {busy
                  ? tLayout('layout.workstatus.saving')
                  : locked
                    ? tLayout('layout.workstatus.locked_off')
                    : tLayout(`layout.workstatus.pick.${key}`)}
              </span>
            </button>
          );
        })}
      </div>

      {error && <p className="status-dropdown-error" role="alert">{error}</p>}
    </div>
  );
}
